const PARENT_OAUTH_TRANSACTION_KEY =
  'mypiggy:parent-oauth-transaction';

const PARENT_OAUTH_TRANSACTION_VERSION = 1;

const PARENT_OAUTH_TRANSACTION_TTL_MS =
  15 * 60 * 1000;

const VALID_OAUTH_FLOWS =
  new Set([
    'login',
    'guardian_invitation',
  ]);

const INVITATION_TOKEN_PATTERN =
  /^[0-9a-f]{64}$/;

const removeStoredTransaction = () => {
  try {
    window.sessionStorage.removeItem(
      PARENT_OAUTH_TRANSACTION_KEY
    );
  } catch (storageError) {
    console.error(
      'Unable to clear parent OAuth transaction:',
      storageError
    );
  }
};

const createTransactionId = () => {
  if (
    typeof window.crypto?.randomUUID ===
    'function'
  ) {
    return window.crypto.randomUUID();
  }

  if (
    typeof window.crypto?.getRandomValues !==
    'function'
  ) {
    throw new Error(
      'Secure browser randomness is unavailable.'
    );
  }

  const bytes = new Uint8Array(16);

  window.crypto.getRandomValues(bytes);

  return Array.from(
    bytes,
    (byte) =>
      byte
        .toString(16)
        .padStart(2, '0')
  ).join('');
};

export const getParentOAuthCallbackUrl =
  () => {
    return new URL(
      '/auth/callback',
      window.location.origin
    ).toString();
  };

export const beginParentOAuthTransaction =
  ({
    flow = 'login',
    inviteToken = null,
  } = {}) => {
    if (!VALID_OAUTH_FLOWS.has(flow)) {
      throw new Error(
        'Invalid parent authentication flow.'
      );
    }

    const normalizedInviteToken =
      typeof inviteToken === 'string'
        ? inviteToken
            .trim()
            .toLowerCase()
        : '';

    if (
      flow === 'guardian_invitation' &&
      !INVITATION_TOKEN_PATTERN.test(
        normalizedInviteToken
      )
    ) {
      throw new Error(
        'A valid Guardian invitation is required.'
      );
    }

    const transaction = {
      version:
        PARENT_OAUTH_TRANSACTION_VERSION,

      id:
        createTransactionId(),

      flow,

      inviteToken:
        flow === 'guardian_invitation'
          ? normalizedInviteToken
          : null,

      createdAt:
        Date.now(),
    };

    try {
      window.sessionStorage.setItem(
        PARENT_OAUTH_TRANSACTION_KEY,
        JSON.stringify(transaction)
      );
    } catch (storageError) {
      console.error(
        'Unable to store parent OAuth transaction:',
        storageError
      );

      throw new Error(
        'Your browser could not save the authentication request. Please try again.'
      );
    }

    return transaction;
  };

export const readParentOAuthTransaction =
  () => {
    let storedValue = null;

    try {
      storedValue =
        window.sessionStorage.getItem(
          PARENT_OAUTH_TRANSACTION_KEY
        );
    } catch (storageError) {
      console.error(
        'Unable to read parent OAuth transaction:',
        storageError
      );

      return null;
    }

    if (!storedValue) {
      return null;
    }

    try {
      const transaction =
        JSON.parse(storedValue);

      const now = Date.now();

      const isValid =
        transaction?.version ===
          PARENT_OAUTH_TRANSACTION_VERSION &&
        typeof transaction?.id ===
          'string' &&
        transaction.id.length >= 16 &&
        VALID_OAUTH_FLOWS.has(
          transaction?.flow
        ) &&
        Number.isFinite(
          transaction?.createdAt
        ) &&
        transaction.createdAt <=
          now + 60 * 1000 &&
        now - transaction.createdAt <=
          PARENT_OAUTH_TRANSACTION_TTL_MS &&
        (
          transaction.flow === 'login' ||
          INVITATION_TOKEN_PATTERN.test(
            transaction?.inviteToken || ''
          )
        );

      if (!isValid) {
        removeStoredTransaction();

        return null;
      }

      return transaction;
    } catch (parseError) {
      console.error(
        'Invalid parent OAuth transaction:',
        parseError
      );

      removeStoredTransaction();

      return null;
    }
  };

export const getParentOAuthReturnPath =
  (transaction) => {
    if (
      transaction?.flow ===
        'guardian_invitation' &&
      INVITATION_TOKEN_PATTERN.test(
        transaction?.inviteToken || ''
      )
    ) {
      return (
        '/parent/invite?token=' +
        encodeURIComponent(
          transaction.inviteToken
        )
      );
    }

    return '/parent/login';
  };

export const clearParentOAuthTransaction =
  (expectedTransactionId = null) => {
    if (expectedTransactionId) {
      const currentTransaction =
        readParentOAuthTransaction();

      if (
        currentTransaction?.id !==
        expectedTransactionId
      ) {
        return false;
      }
    }

    removeStoredTransaction();

    return true;
  };
